import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Separator } from '../components/ui/Separator';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import { format, differenceInDays, isPast } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../context/AppContext';
import { DecisionCard } from '../components/cabinet/DecisionCard';
import { CabinetDecisionModal } from '../components/cabinet/CabinetDecisionModal';
import { NDP6_TAXONOMY } from '../data/ndp6Taxonomy';

/**
 * Ministry lookup for dropdown filters.
 * Matches the 14 ministries (2025 restructuring) plus OP and OPM.
 */
const MINISTRIES = [
  { id: 'MODVA', name: 'Defence and Veterans Affairs' },
  { id: 'MHAISS', name: 'Home Affairs, Immigration, Safety and Security' },
  { id: 'MIRT', name: 'International Relations and Trade' },
  { id: 'MFSGM', name: 'Finance and Social Grants Management' },
  { id: 'MAFWLR', name: 'Agriculture, Fisheries, Water and Land Reform' },
  { id: 'MEIYSC', name: 'Education, Innovation, Youth, Sport, Arts and Culture' },
  { id: 'MHSS', name: 'Health and Social Services' },
  { id: 'MIME', name: 'Industrialisation, Mines and Energy' },
  { id: 'MJL', name: 'Justice and Labour Relations' },
  { id: 'MWTI', name: 'Works, Transport and Infrastructure' },
  { id: 'MEFT', name: 'Environment, Forestry and Tourism' },
  { id: 'MURDH', name: 'Urban and Rural Development, Housing' },
  { id: 'MICT', name: 'Information and Communication Technology' },
  { id: 'GEPSW', name: 'Gender Equality, Poverty Eradication and Social Welfare' },
  { id: 'OP', name: 'Office of the President' },
  { id: 'OPM', name: 'Office of the Prime Minister' },
];

/**
 * Category tab definitions for the filter bar.
 */
const CATEGORY_TABS = [
  { id: 'all', label: 'All', icon: FiIcons.FiList },
  { id: 'critical', label: 'Critical', icon: FiIcons.FiAlertOctagon },
  { id: 'overdue', label: 'Overdue', icon: FiIcons.FiClock },
  { id: 'POLICY', label: 'Policy', icon: FiIcons.FiTarget },
  { id: 'LEGISLATION', label: 'Legislation', icon: FiIcons.FiFileText },
  { id: 'BUDGET', label: 'Budget', icon: FiIcons.FiDollarSign },
  { id: 'EMERGENCY', label: 'Emergency', icon: FiIcons.FiAlertTriangle },
  { id: 'ADMIN', label: 'Admin', icon: FiIcons.FiSettings },
];

/**
 * CabinetDecisions -- list/overview page for all cabinet decisions.
 *
 * Provides filtering by view mode (active/completed/all), category tabs,
 * ministry, priority, NDP6 pillar, and free-text search. Decisions are
 * rendered via the DecisionCard component and clicking through navigates
 * to the detail page.
 */
export const CabinetDecisions = () => {
  const navigate = useNavigate();
  const { data } = useApp();

  // Local state
  const [viewMode, setViewMode] = useState('active');
  const [activeTab, setActiveTab] = useState('all');
  const [ministryFilter, setMinistryFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [pillarFilter, setPillarFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('priority');
  const [showNewModal, setShowNewModal] = useState(false);

  const allDecisions = data.cabinetDecisions || [];
  const allDirectives = data.cabinetDirectives || [];
  const cabinetStats = data.cabinetStats || {};

  /**
   * Compute filtered decisions and aggregate stats from the unfiltered set.
   * All filters stack (AND logic).
   */
  const { filteredDecisions, computedStats } = useMemo(() => {
    let filtered = [...allDecisions];

    // View mode
    if (viewMode === 'active') {
      filtered = filtered.filter(d => d.status !== 'Completed');
    } else if (viewMode === 'completed') {
      filtered = filtered.filter(d => d.status === 'Completed');
    }

    // Category tab
    if (activeTab === 'critical') {
      filtered = filtered.filter(d => d.priority === 'critical');
    } else if (activeTab === 'overdue') {
      filtered = filtered.filter(d => d.status === 'Overdue' || d.overdueDirectives > 0);
    } else if (activeTab !== 'all') {
      filtered = filtered.filter(d => d.category === activeTab);
    }

    // Dropdown filters (stacked)
    if (ministryFilter !== 'all') {
      filtered = filtered.filter(d =>
        d.responsibleMinistries?.some(m => m.ministryId === ministryFilter)
      );
    }
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(d => d.priority === priorityFilter);
    }
    if (pillarFilter !== 'all') {
      filtered = filtered.filter(d => String(d.ndp6PillarId) === pillarFilter);
    }

    // Text search (multiple fields)
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(d =>
        d.title.toLowerCase().includes(q) ||
        d.referenceNumber.toLowerCase().includes(q) ||
        (d.description && d.description.toLowerCase().includes(q)) ||
        d.responsibleMinistries?.some(m => m.ministryName.toLowerCase().includes(q))
      );
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'priority') {
        const order = { critical: 0, high: 1, medium: 2, standard: 3 };
        return (order[a.priority] ?? 4) - (order[b.priority] ?? 4);
      }
      if (sortBy === 'deadline') {
        return new Date(a.deadline || '2099-12-31') - new Date(b.deadline || '2099-12-31');
      }
      if (sortBy === 'dateDecided') {
        return new Date(b.dateDecided || '2000-01-01') - new Date(a.dateDecided || '2000-01-01');
      }
      if (sortBy === 'progress') {
        return (a.overallProgress ?? 0) - (b.overallProgress ?? 0);
      }
      return 0;
    });

    // Compute stats from the unfiltered set
    const total = allDecisions.length;
    const active = allDecisions.filter(d => d.status !== 'Completed').length;
    const completed = allDecisions.filter(d => d.status === 'Completed').length;
    const overdue = allDecisions.filter(d => d.status === 'Overdue' || d.overdueDirectives > 0).length;
    const avgImplRate = cabinetStats.avgImplementationRate ?? (
      total > 0 ? Math.round(allDecisions.reduce((sum, d) => sum + (d.overallProgress ?? 0), 0) / total) : 0
    );

    // Decisions due within 14 days
    const now = new Date();
    const dueWithin14 = allDecisions.filter(d => {
      if (!d.deadline || d.status === 'Completed') return false;
      const days = differenceInDays(new Date(d.deadline), now);
      return days >= 0 && days <= 14;
    }).length;

    return {
      filteredDecisions: filtered,
      computedStats: {
        total,
        active,
        completed,
        overdue,
        implRate: avgImplRate,
        dueWithin14,
      },
    };
  }, [allDecisions, cabinetStats, viewMode, activeTab, ministryFilter, priorityFilter, pillarFilter, searchQuery, sortBy]);

  /**
   * Computes a dynamic count for each category tab based on the current view mode.
   */
  const getTabCount = (tabId) => {
    let base = allDecisions;
    if (viewMode === 'active') base = base.filter(d => d.status !== 'Completed');
    else if (viewMode === 'completed') base = base.filter(d => d.status === 'Completed');

    if (tabId === 'all') return base.length;
    if (tabId === 'critical') return base.filter(d => d.priority === 'critical').length;
    if (tabId === 'overdue') return base.filter(d => d.status === 'Overdue' || d.overdueDirectives > 0).length;
    return base.filter(d => d.category === tabId).length;
  };

  /**
   * Get directives belonging to a specific decision.
   */
  const getDirectivesForDecision = (decisionId) => {
    return allDirectives.filter(d => d.parentDecisionId === decisionId);
  };

  /**
   * Handle stat card click -- sets the appropriate filter.
   */
  const handleStatClick = (statType) => {
    // Reset other filters for a clean focused view
    setSearchQuery('');
    setMinistryFilter('all');
    setPriorityFilter('all');
    setPillarFilter('all');

    switch (statType) {
      case 'total':
        setViewMode('all');
        setActiveTab('all');
        break;
      case 'active':
        setViewMode('active');
        setActiveTab('all');
        break;
      case 'completed':
        setViewMode('completed');
        setActiveTab('all');
        break;
      case 'overdue':
        setViewMode('all');
        setActiveTab('overdue');
        break;
      case 'implRate':
        setViewMode('all');
        setActiveTab('all');
        setSortBy('progress');
        break;
      case 'dueWithin14':
        setViewMode('active');
        setActiveTab('all');
        setSortBy('deadline');
        break;
      default:
        break;
    }
  };

  /** Stats card configuration. */
  const statCards = [
    {
      key: 'total',
      value: computedStats.total,
      label: 'Total Decisions',
      borderColor: 'border-t-slate-400',
      gradient: 'from-white to-slate-50',
      textColor: 'text-foreground',
      icon: FiIcons.FiBookOpen,
    },
    {
      key: 'active',
      value: computedStats.active,
      label: 'Active',
      borderColor: 'border-t-grn-green',
      gradient: 'from-white to-green-50',
      textColor: 'text-green-700',
      icon: FiIcons.FiActivity,
    },
    {
      key: 'completed',
      value: computedStats.completed,
      label: 'Completed',
      borderColor: 'border-t-blue-500',
      gradient: 'from-white to-blue-50',
      textColor: 'text-blue-700',
      icon: FiIcons.FiCheckCircle,
    },
    {
      key: 'overdue',
      value: computedStats.overdue,
      label: 'Overdue',
      borderColor: 'border-t-red-500',
      gradient: 'from-white to-red-50',
      textColor: 'text-red-700',
      icon: FiIcons.FiAlertTriangle,
    },
    {
      key: 'implRate',
      value: `${computedStats.implRate}%`,
      label: 'Implementation Rate',
      borderColor: 'border-t-grn-blue',
      gradient: 'from-white to-sky-50',
      textColor: 'text-grn-blue',
      icon: FiIcons.FiTrendingUp,
    },
    {
      key: 'dueWithin14',
      value: computedStats.dueWithin14,
      label: 'Due Within 14 Days',
      borderColor: 'border-t-amber-500',
      gradient: 'from-white to-amber-50',
      textColor: 'text-amber-700',
      icon: FiIcons.FiClock,
    },
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* ------------------------------------------------------------------ */}
      {/* Hero Header                                                        */}
      {/* ------------------------------------------------------------------ */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-grn-navy via-blue-900 to-slate-900 p-8 shadow-2xl">
        {/* Decorative pattern overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtNi42MjcgNS4zNzMtMTIgMTItMTJzMTIgNS4zNzMgMTIgMTItNS4zNzMgMTItMTIgMTItMTItNS4zNzMtMTItMTJ6bS0xMiAwYzAtNi42MjcgNS4zNzMtMTIgMTItMTJzMTIgNS4zNzMgMTIgMTItNS4zNzMgMTItMTIgMTItMTItNS4zNzMtMTItMTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20" />

        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="flex-1">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-blue-300/70 text-xs font-medium mb-4">
              <SafeIcon icon={FiIcons.FiHome} className="text-sm" />
              <span>GovTrack</span>
              <SafeIcon icon={FiIcons.FiChevronRight} className="text-xs" />
              <span className="text-white">Cabinet Decisions</span>
            </div>

            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                <SafeIcon icon={FiIcons.FiBookOpen} className="text-3xl text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-black text-white tracking-tight">
                  Cabinet Decisions
                </h1>
                <p className="text-blue-200 mt-1 text-sm font-medium">
                  Tracking implementation of all Cabinet resolutions and directives across government
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              size="default"
              className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20"
            >
              <SafeIcon icon={FiIcons.FiFilter} />
              Advanced Filters
            </Button>
            <Button
              variant="outline"
              size="default"
              className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20"
            >
              <SafeIcon icon={FiIcons.FiDownload} />
              Export Report
            </Button>
            <Button
              variant="default"
              size="default"
              className="bg-white text-grn-navy hover:bg-blue-50 shadow-lg shadow-white/20"
              onClick={() => setShowNewModal(true)}
            >
              <SafeIcon icon={FiIcons.FiPlus} />
              New Decision
            </Button>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Stats Row                                                          */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {statCards.map((stat) => (
          <Card
            key={stat.key}
            className={clsx(
              'text-center p-6 hover:shadow-xl transition-all duration-300 cursor-pointer',
              'border-t-4',
              stat.borderColor,
              `bg-gradient-to-br ${stat.gradient}`
            )}
            onClick={() => handleStatClick(stat.key)}
          >
            <div className="flex justify-center mb-2">
              <SafeIcon icon={stat.icon} className={clsx('text-lg', stat.textColor)} />
            </div>
            <div className={clsx('text-4xl font-black mb-2', stat.textColor)}>
              {stat.value}
            </div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider font-bold">
              {stat.label}
            </div>
          </Card>
        ))}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* View Mode Selector                                                 */}
      {/* ------------------------------------------------------------------ */}
      <Card className="bg-gradient-to-br from-white to-slate-50 shadow-lg">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">
            View Mode
          </h3>
          <div className="flex gap-2">
            {[
              { id: 'active', label: 'Active Decisions', icon: FiIcons.FiActivity, count: computedStats.active },
              { id: 'completed', label: 'Completed', icon: FiIcons.FiCheckCircle, count: computedStats.completed },
              { id: 'all', label: 'All Decisions', icon: FiIcons.FiList, count: computedStats.total },
            ].map((mode) => (
              <Button
                key={mode.id}
                onClick={() => setViewMode(mode.id)}
                variant={viewMode === mode.id ? 'default' : 'outline'}
                size="default"
                className={viewMode === mode.id ? 'shadow-lg' : ''}
              >
                <SafeIcon icon={mode.icon} />
                {mode.label}
                <span
                  className={clsx(
                    'ml-2 px-2.5 py-0.5 rounded-full text-xs font-bold',
                    viewMode === mode.id ? 'bg-white/20' : 'bg-muted'
                  )}
                >
                  {mode.count}
                </span>
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Filter Card: Category Tabs + Dropdowns + Search                    */}
      {/* ------------------------------------------------------------------ */}
      <Card className="bg-gradient-to-br from-white to-slate-50 shadow-lg">
        <div className="space-y-4">
          {/* Category Tabs */}
          <div>
            <h3 className="text-sm font-bold text-foreground mb-3 uppercase tracking-wider">
              Filter by Category
            </h3>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {CATEGORY_TABS.map((tab) => (
                <Button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  variant={activeTab === tab.id ? 'default' : 'outline'}
                  size="sm"
                  className={clsx(
                    'whitespace-nowrap transition-all duration-300',
                    activeTab === tab.id && 'shadow-lg scale-105'
                  )}
                >
                  <SafeIcon icon={tab.icon} />
                  {tab.label}
                  <span
                    className={clsx(
                      'ml-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold',
                      activeTab === tab.id ? 'bg-white/20' : 'bg-muted'
                    )}
                  >
                    {getTabCount(tab.id)}
                  </span>
                </Button>
              ))}
            </div>
          </div>

          <Separator />

          {/* Dropdown Filters Row */}
          <div>
            <h3 className="text-sm font-bold text-foreground mb-3 uppercase tracking-wider">
              Refine Results
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Ministry Filter */}
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1.5">
                  Ministry
                </label>
                <Select
                  value={ministryFilter}
                  onChange={(e) => setMinistryFilter(e.target.value)}
                  className="shadow-sm"
                >
                  <option value="all">All Ministries</option>
                  {MINISTRIES.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.id} -- {m.name}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1.5">
                  Priority
                </label>
                <Select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="shadow-sm"
                >
                  <option value="all">All Priorities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="standard">Standard</option>
                </Select>
              </div>

              {/* NDP6 Pillar Filter */}
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1.5">
                  NDP6 Pillar
                </label>
                <Select
                  value={pillarFilter}
                  onChange={(e) => setPillarFilter(e.target.value)}
                  className="shadow-sm"
                >
                  <option value="all">All Pillars</option>
                  {NDP6_TAXONOMY.pillars.map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          <Separator />

          {/* Search Input */}
          <div className="relative">
            <SafeIcon
              icon={FiIcons.FiSearch}
              className="absolute left-3 top-3 text-muted-foreground"
            />
            <Input
              type="text"
              placeholder="Search by title, reference number, description, or ministry name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 text-base shadow-sm"
            />
          </div>
        </div>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Results Header                                                     */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex justify-between items-center">
        <h2 className="text-sm font-bold text-foreground uppercase tracking-wider">
          Showing {filteredDecisions.length} decision{filteredDecisions.length !== 1 ? 's' : ''}
          {viewMode === 'completed' && ' (Completed)'}
          {viewMode === 'active' && ' (Active)'}
        </h2>
        <Select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="w-[260px] shadow-sm"
        >
          <option value="priority">Sort by: Priority (High to Low)</option>
          <option value="deadline">Sort by: Deadline (Earliest First)</option>
          <option value="dateDecided">Sort by: Date Decided (Recent First)</option>
          <option value="progress">Sort by: Progress (Lowest First)</option>
        </Select>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Decision Cards                                                     */}
      {/* ------------------------------------------------------------------ */}
      {filteredDecisions.length > 0 ? (
        <div className="space-y-4">
          <AnimatePresence mode="popLayout">
            {filteredDecisions.map((decision) => (
              <DecisionCard
                key={decision.id}
                decision={decision}
                directives={getDirectivesForDecision(decision.id)}
                variant="full"
                onViewDetails={(d) => navigate(`/cabinet-decisions/${d.id}`)}
                onViewDirectives={(d) => navigate(`/cabinet-decisions/${d.id}`)}
                onEscalate={(d) => {
                  // Future: open escalation modal
                }}
              />
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <Card className="text-center py-16 bg-gradient-to-br from-white to-slate-50 shadow-lg">
          <div className="flex flex-col items-center justify-center text-muted-foreground">
            <div className="p-6 bg-muted/30 rounded-full mb-4">
              <SafeIcon icon={FiIcons.FiInbox} className="text-6xl" />
            </div>
            <h3 className="text-xl font-black text-foreground mb-2">
              No cabinet decisions found
            </h3>
            <p className="text-sm mb-4">
              Try adjusting your filters, category selection, or search query.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setViewMode('all');
                setActiveTab('all');
                setMinistryFilter('all');
                setPriorityFilter('all');
                setPillarFilter('all');
                setSearchQuery('');
                setSortBy('priority');
              }}
            >
              <SafeIcon icon={FiIcons.FiRefreshCw} />
              Reset All Filters
            </Button>
          </div>
        </Card>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Legal Framework Notice Footer                                      */}
      {/* ------------------------------------------------------------------ */}
      <Card className="bg-gradient-to-br from-grn-navy to-blue-900 text-white shadow-2xl">
        <div className="flex gap-4">
          <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 shrink-0">
            <SafeIcon icon={FiIcons.FiInfo} className="text-2xl" />
          </div>
          <div className="text-xs leading-relaxed">
            <p className="font-black mb-3 text-base uppercase tracking-wider">
              Cabinet Decisions Legal Framework
            </p>
            <p className="opacity-90">
              Cabinet decisions are governed by the <strong>Cabinet Manual (2015)</strong> and the{' '}
              <strong>Constitution of the Republic of Namibia (1990), Chapter 6 (Articles 35-42)</strong>.
              All Cabinet Decision Numbers (CDNs) are issued by the Secretary to the Cabinet and
              tracked through the National Implementation Monitoring System. Responsible Offices,
              Ministries, and Agencies (O/M/As) must acknowledge receipt within{' '}
              <strong>7 days</strong> and submit implementation plans within{' '}
              <strong>14 days</strong> of communication. Quarterly progress reports are due to the
              Cabinet Secretariat within <strong>90 days</strong>. Failure to comply may result in
              escalation to the Prime Minister and referral to parliamentary oversight committees.
            </p>
          </div>
        </div>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* New Decision Modal                                                 */}
      {/* ------------------------------------------------------------------ */}
      <CabinetDecisionModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        mode="decision"
      />
    </div>
  );
};
